import Vue from 'vue';
import Vuex from 'vuex';
import { constantRoutes, blankRoutes, resetRouter } from '@/router';
import { filterRoutes } from '@/utils/routes';
import { unique } from '@/utils/tools';
import cloneDeep from 'lodash/cloneDeep';
import { selectUserType } from '@/api/process/tool';
import {
  pinyinAbbreviation,
  titleCase,
} from '@/views/process/engine/components/EngineForm/utils';
import { sysPlatformType } from '@/config/edition.config';
import uniqBy from 'lodash/uniqBy';
import { teacherEngineList } from '@/api/process/engine';
import { queryXxxsBylrlb } from '@/api/jxjy/learningInput';

Vue.use(Vuex);

export default {
  state: {
    routes: [],
    activeName: '', // 当前激活菜单
  },
  getters: {
    routes: (state) => state.routes,
    activeName: (state) => state.activeName,
  },
  mutations: {
    setRoutes(state, routes) {
      state.routes = routes;
    },
  },
  actions: {
    async setRoutes({ rootGetters, commit, dispatch }) {
      let accessRoutes;
      if (rootGetters.userInfo.xm) {
        // 默认前端路由
        const asyncModules = require.context(
          '@/router/asyncRoutes',
          true,
          /\.js$/
        );
        let routes = asyncModules
          .keys()
          .map(asyncModules)
          .sort((a, b) => a.order - b.order)
          .map((item) => item.routes);

        // 处理动态流程审批路由
        if (
          rootGetters.admin ||
          rootGetters.permission.includes('upms:statisticalSummary')
        ) {
          // 统计汇总
          const statisticalSummaryRoute = routes.find(
            (item) => item.name === 'statisticalSummary'
          );

          let perms = cloneDeep(rootGetters.dynamicPerm);

          const postId = rootGetters.admin
            ? undefined
            : rootGetters.currentRole.postId;
          const approvalList = await dispatch('getEngineList', postId, {
            root: true,
          });
          // 处理指定的动态流程审批路由
          const handleSpecifyRoute = (
            route,
            engine,
            path,
            title = '',
            activeMenu = ''
          ) => {
            if (route) {
              const perm = route.meta.guard.permission[0] + ':' + path;
              route.children.splice(route.children.length, 1, {
                path: `${engine.userType}/${engine.formType}/${engine.id}`,
                name: path.toUpperCase(),
                component: () =>
                  import('@/views/statisticalSummary/departmental.vue'),
                meta: {
                  title: title || engine.name,
                  // 适应全局样式，显示面包屑
                  // breadcrumbHidden: true,
                  keepAlive: true,
                  guard: {
                    permission: [perm],
                  },
                  activeMenu,
                  hidden: !!activeMenu,
                },
              });
              route.children = uniqBy(route.children, 'name');

              perms[path.toUpperCase()] = [
                { title: '删除', permission: `${perm}:del` },
                { title: '归档', permission: `${perm}:archive` },
              ];
              // 统计汇总只展示流程监控，因此没有以下权限点
              /*if (engine.formType < 3) {
                perms[path.toUpperCase()].push(
                  { title: '审批', permission: `${perm}:audit` },
                  { title: '撤销', permission: `${perm}:quash` },
                  {
                    title: '批量导入填报信息',
                    permission: `${perm}:importFilledInfo`,
                  },
                  {
                    title: '批量导入审批结果',
                    permission: `${perm}:importApprovalResults`,
                  }
                );
                if (sysPlatformType === '2') {
                  perms[path.toUpperCase()].push({
                    title: '批量导入申请信息',
                    permission: `${perm}:importApplicationInfo`,
                  });
                }
              }*/
              perms[path.toUpperCase()] = uniqBy(
                perms[path.toUpperCase()],
                'permission'
              );
            }
          };

          const res = await queryXxxsBylrlb({
            xxnd: new Date().getFullYear().toString(),
          });
          if (res?.code === 1 && res?.data?.[0]) {
            const lrlbOrder = ['公需科目必修课', '公需科目选修课', '专业科目'];
            const sortedData = [...res.data].sort((a, b) => {
              const indexA = lrlbOrder.indexOf(a.lrlb);
              const indexB = lrlbOrder.indexOf(b.lrlb);
              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
              }
              if (indexA !== -1) return -1;
              if (indexB !== -1) return 1;
              return 0;
            });
            for (const item of sortedData) {
              // 流程分类菜单
              let parentIndex = statisticalSummaryRoute.children.findIndex(
                (route) => route.meta.title === item.lrlb
              );
              if (parentIndex === -1) {
                const categoryPath = pinyinAbbreviation(item.lrlb);
                statisticalSummaryRoute.children.splice(
                  statisticalSummaryRoute.children.length,
                  1,
                  {
                    path: 'monitoring' + item.flowId,
                    name: categoryPath.toUpperCase(),
                    meta: {
                      title: item.lrlb,
                      guard: {
                        permission:
                          statisticalSummaryRoute.meta.guard.permission,
                      },
                    },
                    children: [],
                  }
                );
                statisticalSummaryRoute.children = uniqBy(
                  statisticalSummaryRoute.children,
                  'name'
                );
                parentIndex = statisticalSummaryRoute.children.length - 1;
              }
              const matter = approvalList.find(
                (matter) => matter.id === item.flowId
              );
              if (matter) {
                let enginePath = pinyinAbbreviation(matter.name);
                enginePath = enginePath.replace(/[^a-zA-Z0-9]/g, '_');
                handleSpecifyRoute(
                  statisticalSummaryRoute.children[parentIndex],
                  matter,
                  enginePath
                );
              }
            }
          }

          dispatch('setDynamicPerm', perms, { root: true });
        }

        // 处理动态用户类型的流程设计路由
        const processRoutes = routes.find(
          (item) => item.name === 'ProcessManagement'
        ).children;
        let processDesignRoutes = {
          ProcessDesign: {
            perm: processRoutes.find((item) => item.name === 'ProcessDesign')
              .meta.guard.permission[0],
            children: [],
            title: '流程',
          },
          ProcessSystemDesign: {
            perm: processRoutes.find(
              (item) => item.name === 'ProcessSystemDesign'
            ).meta.guard.permission[0],
            children: [],
            title: '流程',
          },
          ProcessFormDesign: {
            perm: processRoutes.find(
              (item) => item.name === 'ProcessFormDesign'
            ).meta.guard.permission[0],
            children: [],
            title: '表单',
          },
          ProcessQuestionnaireDesign: {
            perm: processRoutes.find(
              (item) => item.name === 'ProcessQuestionnaireDesign'
            ).meta.guard.permission[0],
            children: [],
            title: '问卷',
          },
        };
        const processDesignPerms = Object.keys(processDesignRoutes).map(
          (key) => processDesignRoutes[key].perm
        );
        if (
          rootGetters.admin ||
          processDesignPerms.some((item) =>
            rootGetters.permission.includes(item)
          )
        ) {
          const res = await selectUserType();
          if (res && res.code === 1 && res.data) {
            let perms = cloneDeep(rootGetters.dynamicPerm);
            res.data.forEach((userTypeItem) => {
              const userType = userTypeItem.en
                .replace('ACCOUNT', '')
                .toLowerCase();
              Object.keys(processDesignRoutes).forEach((key, index) => {
                processDesignRoutes[key].children.push({
                  path: userType,
                  name: key + titleCase(userType),
                  component: () => import('@/views/process/design'),
                  meta: {
                    title: userTypeItem.name,
                    userType: userTypeItem.id,
                    breadcrumbHidden: true,
                    guard: {
                      permission: [`${processDesignPerms[index]}:${userType}`],
                    },
                  },
                });
              });
            });
            blankRoutes.find(
              (item) => item.name === 'ProcessEngine'
            ).meta.guard.permission = [];
            Object.keys(processDesignRoutes).forEach((key) => {
              const uniqueDesignChildren = unique(
                processDesignRoutes[key].children,
                'name'
              );
              if (uniqueDesignChildren[0]) {
                const designRouteChildren = routes
                  .find((item) => item.name === 'ProcessManagement')
                  .children.find((item) => item.name === key).children;
                designRouteChildren.splice(
                  0,
                  designRouteChildren.length,
                  ...uniqueDesignChildren
                );
              }
              uniqueDesignChildren.forEach((item) => {
                perms[item.name] = [
                  {
                    title: `新增${processDesignRoutes[key].title}`,
                    permission: `${item.meta.guard.permission[0]}:add`,
                  },
                  {
                    title: '删除',
                    permission: `${item.meta.guard.permission[0]}:del`,
                  },
                  {
                    title: '基本设置',
                    permission: `${item.meta.guard.permission[0]}:editBase`,
                  },
                  {
                    title: '流程设计',
                    permission: `${item.meta.guard.permission[0]}:editProcess`,
                  },
                  {
                    title: '审批转交',
                    permission: `${item.meta.guard.permission[0]}:handover`,
                  },
                  {
                    title: '初始化',
                    permission: `${item.meta.guard.permission[0]}:init`,
                  },
                  {
                    title: '清空数据',
                    permission: `${item.meta.guard.permission[0]}:clear`,
                  },
                  {
                    title: '更新IP',
                    permission: `${item.meta.guard.permission[0]}:ip`,
                  },
                ];
                if (key !== 'ProcessSystemDesign') {
                  blankRoutes
                    .find((item) => item.name === 'ProcessEngine')
                    .meta.guard.permission.push(
                      `${item.meta.guard.permission[0]}:add`,
                      `${item.meta.guard.permission[0]}:editBase`,
                      `${item.meta.guard.permission[0]}:editProcess`
                    );
                }
              });
            });
            dispatch('setDynamicPerm', perms, { root: true });
          }
        }

        // 根据权限和rolesControl过滤路由
        accessRoutes = filterRoutes([
          ...constantRoutes,
          ...blankRoutes,
          ...routes,
        ]);
      } else {
        accessRoutes = filterRoutes([...constantRoutes, ...blankRoutes]);
      }

      // 设置菜单所需路由
      commit('setRoutes', JSON.parse(JSON.stringify(accessRoutes)));

      // 根据可访问路由重置Vue Router
      await resetRouter(accessRoutes);
    },
  },
};
